import { useEffect, useRef, useState } from "react";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      el.classList.add("in");
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px 8% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

// Drop-in <img> replacement that prefers a same-name .webp via <picture>.
// Pass a JPEG src; the WebP is derived. Falls through to a plain <img> if the
// src isn't a JPEG (so external URLs and SVGs keep working).
export function Picture({
  src,
  alt,
  className,
  style,
  loading = "lazy",
  decoding = "async",
  onError,
  ...rest
}) {
  const isJpeg = typeof src === "string" && /\.jpe?g$/i.test(src);
  const img = (
    <img src={src} alt={alt} loading={loading} decoding={decoding} onError={onError} {...rest} />
  );
  if (!isJpeg) return img;
  const webp = src.replace(/\.jpe?g$/i, ".webp");
  return (
    <picture className={className} style={style}>
      <source srcSet={webp} type="image/webp" />
      {img}
    </picture>
  );
}

export function Reveal({ children, delay = 0, as: Tag = "div", className = "", style, ...rest }) {
  const ref = useReveal();
  return (
    <Tag
      ref={ref}
      className={`reveal ${className}`}
      style={{ ...style, "--reveal-delay": `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function Eyebrow({ index, children }) {
  return (
    <span className="eyebrow">
      {index ? (
        <span className="mono" style={{ color: "var(--gold)", opacity: 0.7 }}>
          {index}
        </span>
      ) : null}
      {children}
    </span>
  );
}

export function Stitch({ style }) {
  return <div className="stitch" style={style} />;
}

export function Placeholder({ label, ratio = "16/9", style, className = "" }) {
  return (
    <div className={`ph ${className}`} style={{ aspectRatio: ratio, ...style }}>
      <div className="ph-label">{label || "Photo"}</div>
    </div>
  );
}

export function ImgPlate({ src, alt, caption, ratio = "16/9", style, className = "" }) {
  return (
    <div className={`imgwrap ${className}`} style={{ aspectRatio: ratio, ...style }}>
      <Picture src={src} alt={alt || ""} />
      {caption ? <div className="caption">{caption}</div> : null}
    </div>
  );
}

export function Btn({ children, primary, ghost, href, onClick, type, target, rel }) {
  const cls = `btn ${primary ? "btn-primary" : ghost ? "btn-ghost" : ""}`;
  const arrow = (
    <span className="btn-arrow" aria-hidden>
      →
    </span>
  );
  if (href) {
    return (
      <a
        className={cls}
        href={href}
        onClick={onClick}
        target={target}
        rel={rel || (target === "_blank" ? "noopener" : undefined)}
      >
        {children} {arrow}
      </a>
    );
  }
  return (
    <button className={cls} onClick={onClick} type={type || "button"}>
      {children} {arrow}
    </button>
  );
}

export function ScrollProgress() {
  const ref = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      const p = max > 0 ? h.scrollTop / max : 0;
      if (ref.current) ref.current.style.transform = `scaleX(${p})`;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return <div className="scroll-progress" ref={ref} />;
}

export function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const onScroll = () => {
      const triggerY = window.innerHeight * 0.3;
      let bestId = ids[0];
      let bestTop = -Infinity;
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= triggerY && rect.top > bestTop) {
          bestTop = rect.top;
          bestId = id;
        }
      });
      setActive((prev) => (prev === bestId ? prev : bestId));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ids.join(",")]);
  return active;
}

export function useSectionInView() {
  useEffect(() => {
    const supportsTimeline =
      typeof CSS !== "undefined" && CSS.supports && CSS.supports("animation-timeline: view()");
    if (supportsTimeline) return;
    const sections = document.querySelectorAll("section.section:not(.hero)");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("in-view");
        });
      },
      { threshold: 0.04, rootMargin: "0px 0px 12% 0px" },
    );
    sections.forEach((s) => {
      obs.observe(s);
    });
    return () => obs.disconnect();
  }, []);
}

export function useParallax(speed = 0.18) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) return;
    let raf = 0;
    const tick = () => {
      const rect = el.getBoundingClientRect();
      const center = rect.top + rect.height / 2 - window.innerHeight / 2;
      el.style.transform = `translate3d(0, ${center * -speed}px, 0)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [speed]);
  return ref;
}

// Lock the page behind a full-screen modal *without moving it*. We freeze the
// scroll root with overflow:hidden rather than position:fixed + a negative top
// offset: that offset is supposed to be visually seamless, but Safari doesn't
// always hold it, so the background visibly jumps on open and again on close.
// overflow:hidden never repositions anything, so the page stays exactly where
// it is. (On macOS/iOS the scrollbar is an overlay, so hiding it shifts nothing.)
export function useScrollLock() {
  useEffect(() => {
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      root.style.overflow = prevOverflow;
    };
  }, []);
}

// Deep-linking to a section. A URL like /#gallery should land on
// that section, and editing/sharing the hash should scroll to it. Because the
// page is client-rendered, the browser's native hash jump fires before React
// has mounted the sections, so it no-ops — we re-apply the hash here once the
// DOM exists. (CSS already sets `scroll-padding-top: var(--nav-offset)` on
// <html>, so the fixed nav never overlaps the section heading.) This also makes
// ids that aren't in the menu — #mytake, #health, #facebook — linkable.
export function useHashScroll() {
  useEffect(() => {
    const targetEl = () => {
      const id = decodeURIComponent(window.location.hash.replace(/^#/, ""));
      return id ? document.getElementById(id) : null;
    };

    const root = document.documentElement;
    let release = () => {};
    // Deep link on a fresh load. The page is client-rendered and its images are
    // lazy and mostly unsized, so the target's position keeps shifting for a beat
    // after mount — a one-shot scroll lands short (or long) on sections far down
    // the page. Re-pin the target to the top on every frame for a short window so
    // any reflow (web fonts swapping in, the book cover decoding) is corrected as
    // it happens. Smooth scrolling is disabled for the window: in a per-frame
    // loop the global `scroll-behavior: smooth` would restart an animation every
    // frame and never arrive. We release the instant the reader scrolls, so we
    // never trap them, and stop after ~180 frames (~3s) regardless.
    if (window.location.hash && targetEl()) {
      let active = true;
      let frame = 0;
      const prevBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = "auto";
      const userEvents = ["wheel", "touchstart", "keydown", "pointerdown"];
      release = () => {
        if (!active) return;
        active = false;
        root.style.scrollBehavior = prevBehavior;
        for (const ev of userEvents) window.removeEventListener(ev, release);
      };
      const tick = () => {
        if (!active) return;
        const el = targetEl();
        if (el) el.scrollIntoView({ block: "start" });
        if (++frame < 180) requestAnimationFrame(tick);
        else release();
      };
      for (const ev of userEvents) window.addEventListener(ev, release, { passive: true });
      requestAnimationFrame(tick);
    }

    // Hash changes during the session (an in-page #link, a manual edit, a shared
    // link opened while already here) scroll smoothly. history.replaceState from
    // the nav does NOT fire this, so menu clicks never double-jump.
    const onHashChange = () => {
      const el = targetEl();
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    window.addEventListener("hashchange", onHashChange);
    return () => {
      release();
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);
}
